// src/hooks/useChartData.js
import { useMemo } from "react";
import { ALL_MONTHS } from "../utils/constants";
import { formatLocalDate } from "../utils/dateHelpers";

/**
 * Hook for monthly Trial/Paid account creation data (used in the main bar chart)
 */
export const useMonthlyChartData = (analyticsData, chartDateRange) => {
  return useMemo(() => {
    if (!analyticsData?.active_accounts) {
      return {
        months: [],
        trialCounts: [],
        paidCounts: [],
        monthlyRawData: [],
        monthlyBarData: [],
        isDefault: true,
      };
    }

    const [startDate, endDate] = chartDateRange || [null, null];
    const isDefault = !startDate && !endDate;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Default: Current year, up to current month
    if (isDefault) {
      const trialCounts = new Array(12).fill(0);
      const paidCounts = new Array(12).fill(0);

      analyticsData.active_accounts.forEach((item) => {
        const date = new Date(item.created_at);
        if (date.getFullYear() === currentYear) {
          const monthIdx = date.getMonth();
          if (monthIdx <= currentMonth) {
            if (item.account_type === "Trial") trialCounts[monthIdx]++;
            else if (item.account_type === "Paid") paidCounts[monthIdx]++;
          }
        }
      });

      const visibleMonths = ALL_MONTHS.slice(0, currentMonth + 1);
      const finalTrial = trialCounts.slice(0, currentMonth + 1);
      const finalPaid = paidCounts.slice(0, currentMonth + 1);

      const rawData = visibleMonths.map((month, i) => ({
        month,
        Trial: finalTrial[i],
        Paid: finalPaid[i],
      }));

      return {
        months: visibleMonths,
        trialCounts: finalTrial,
        paidCounts: finalPaid,
        monthlyRawData: rawData,
        monthlyBarData: rawData, // Your CustomBarChart expects this shape
        isDefault: true,
      };
    }

    // Custom range
    const start = startDate ? new Date(startDate) : null;
    const effectiveEnd = endDate ? new Date(endDate) : new Date();
    effectiveEnd.setHours(23, 59, 59, 999);

    if (!start) {
      return {
        months: [],
        trialCounts: [],
        paidCounts: [],
        monthlyRawData: [],
        monthlyBarData: [],
        isDefault: false,
      };
    }

    const resultMonths = [];
    const resultTrial = [];
    const resultPaid = [];

    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const endCursor = new Date(effectiveEnd.getFullYear(), effectiveEnd.getMonth() + 1, 0);

    while (cursor <= endCursor) {
      const year = cursor.getFullYear();
      const monthIdx = cursor.getMonth();
      const label = `${ALL_MONTHS[monthIdx]} ${year}`;

      let trialCount = 0;
      let paidCount = 0;

      analyticsData.active_accounts.forEach((item) => {
        const itemDate = new Date(item.created_at);
        if (
          itemDate.getFullYear() === year &&
          itemDate.getMonth() === monthIdx &&
          itemDate >= start &&
          itemDate <= effectiveEnd
        ) {
          if (item.account_type === "Trial") trialCount++;
          else if (item.account_type === "Paid") paidCount++;
        }
      });

      resultMonths.push(label);
      resultTrial.push(trialCount);
      resultPaid.push(paidCount);

      cursor.setMonth(cursor.getMonth() + 1);
    }

    const rawData = resultMonths.map((month, i) => ({
      month,
      Trial: resultTrial[i],
      Paid: resultPaid[i],
    }));

    return {
      months: resultMonths,
      trialCounts: resultTrial,
      paidCounts: resultPaid,
      monthlyRawData: rawData,
      monthlyBarData: rawData,
      isDefault: false,
    };
  }, [analyticsData, chartDateRange]);
};

/**
 * Hook for daily Trial/Paid account creation (used in modal)
 */
export const useDailyAccountCreationData = (analyticsData, chartDateRange) => {
  return useMemo(() => {
    if (!analyticsData?.active_accounts || !chartDateRange[0] || !chartDateRange[1]) {
      return [];
    }

    const [startDate, endDate] = chartDateRange;
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const dailyMap = {};

    analyticsData.active_accounts.forEach((item) => {
      const date = new Date(item.created_at);
      if (date >= start && date <= end) {
        const key = formatLocalDate(date);
        if (!dailyMap[key]) {
          dailyMap[key] = { trial: 0, paid: 0 };
        }
        if (item.account_type === "Trial") dailyMap[key].trial++;
        else if (item.account_type === "Paid") dailyMap[key].paid++;
      }
    });

    const result = [];
    const current = new Date(start);
    while (current <= end) {
      const key = formatLocalDate(current);
      result.push({
        date: key,
        trial: dailyMap[key]?.trial || 0,
        paid: dailyMap[key]?.paid || 0,
      });
      current.setDate(current.getDate() + 1);
    }

    return result;
  }, [analyticsData, chartDateRange]);
};

/**
 * Hook for expiring accounts segments (0–7, 8–15, 16–30 days)
 */
export const useExpiringSegments = (expiringAdmins) => {
  return useMemo(() => {
    if (!expiringAdmins || expiringAdmins.length === 0) {
      return [
        { name: "0-7 Days", value: 0 },
        { name: "8-15 Days", value: 0 },
        { name: "16-30 Days", value: 0 },
      ];
    }

    const now = new Date();
    const in7 = new Date(now); in7.setDate(now.getDate() + 7);
    const in15 = new Date(now); in15.setDate(now.getDate() + 15);
    const in30 = new Date(now); in30.setDate(now.getDate() + 30);

    let count0to7 = 0, count8to15 = 0, count16to30 = 0;

    expiringAdmins.forEach((admin) => {
      const end = new Date(admin.end_date);
      if (end <= in7) count0to7++;
      else if (end <= in15) count8to15++;
      else if (end <= in30) count16to30++;
    });

    return [
      { name: "0-7 Days", value: count0to7 },
      { name: "8-15 Days", value: count8to15 },
      { name: "16-30 Days", value: count16to30 },
    ];
  }, [expiringAdmins]);
};